import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";

// Services & Types
import { ContributionService } from "services/contribution.service";
import { MemberService } from "services/member.service";
import { ContributionTypeService } from "services/contribution-type.service";
import {
  ISaveContribution,
  IUpdateContribution,
  IMemberResponseDTO,
  IContributionTypeResponse,
} from "models/church.types";

export default function ContributionRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<{ value: number; label: string }[]>(
    [],
  );
  const [types, setTypes] = useState<{ value: number; label: string }[]>([]);
  const [errors, setErrors] = useState<{
    memberId?: string;
    typeId?: string;
    amount?: string;
  }>({});

  const [contribution, setContribution] = useState<any>({
    amount: 0,
    paymentDate: new Date().toISOString().split("T")[0],
    observation: "",
    memberId: undefined,
    typeId: undefined,
  });

  // Styles personnalisés react-select (Alignés sur le design UI et sécurisés par Portal)
  const customSelectStyles = (hasError: boolean) => ({
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#0F172A",
      borderColor: state.isFocused
        ? "#D4AF37"
        : hasError
          ? "#ef4444"
          : "#334155",
      borderRadius: "0.5rem",
      padding: "3px",
      minHeight: "45px",
      color: "white",
      boxShadow: "none",
      transition: "all 0.2s",
      "&:hover": { borderColor: "#D4AF37" },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      zIndex: 9999,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999, // Force la superposition absolue au niveau du DOM global
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#D4AF37" : "transparent",
      color: state.isFocused ? "#0F172A" : "white",
      padding: "10px",
      cursor: "pointer",
      fontSize: "0.875rem",
      "&:active": { backgroundColor: "#D4AF37" },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
      fontSize: "0.875rem",
    }),
    input: (base: any) => ({ ...base, color: "white" }),
    placeholder: (base: any) => ({
      ...base,
      color: "#64748b",
      fontSize: "0.875rem",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#475569",
      "&:hover": { color: "#D4AF37" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [membersRes, typesRes] = await Promise.all([
          MemberService.getAll(0, 1000),
          ContributionTypeService.getAll(0, 1000),
        ]);

        // Formatage basé sur IMemberResponseDTO
        setMembers(
          membersRes.data.map((m: IMemberResponseDTO) => ({
            value: m.id,
            label:
              `${m.lastName || ""} ${m.firstName || ""}`.trim() ||
              `Membre #${m.id}`,
          })) || [],
        );

        setTypes(
          typesRes.data.map((t: IContributionTypeResponse) => ({
            value: t.id,
            label: t.name,
          })) || [],
        );

        if (isEditMode && id) {
          const data = await ContributionService.getById(parseInt(id));
          setContribution({
            id: data.id,
            amount: data.amount,
            paymentDate: data.paymentDate ? data.paymentDate.split("T")[0] : "",
            observation: data.observation || "",
            memberId: data.memberId,
            typeId: data.typeId,
          });
        }
      } catch (error) {
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode]);

  const validate = (): boolean => {
    const newErrors: any = {};
    if (!contribution.memberId) newErrors.memberId = "Membre requis";
    if (!contribution.typeId) newErrors.typeId = "Type requis";
    if (contribution.amount <= 0)
      newErrors.amount = "Le montant doit être supérieur à 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setContribution((prev: any) => ({
      ...prev,
      [name]: name === "amount" ? (value ? Number(value) : 0) : value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        ...contribution,
        paymentDate: new Date(contribution.paymentDate).toISOString(),
      };

      if (isEditMode) {
        await ContributionService.update(payload as IUpdateContribution);
        toast.success("Contribution mise à jour");
      } else {
        await ContributionService.create(payload as ISaveContribution);
        toast.success("Contribution enregistrée");
      }
      history.push("/admin/contributions");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement technique.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-start md:justify-center mt-14 p-4 w-full min-h-screen py-12 md:py-6">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl overflow-hidden">
          <div className="bg-[#0F172A] px-6 py-4 border-b border-slate-700 text-white flex justify-between items-center">
            <h6 className="text-sm font-bold uppercase tracking-widest">
              <i className="fas fa-hand-holding-usd mr-2 text-[#D4AF37]"></i>
              {isEditMode
                ? "Modifier la contribution"
                : "Nouvelle contribution"}
            </h6>
          </div>

          <div className="flex-auto px-6 py-8 text-slate-300">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Membre (Select Intelligent avec Portal) */}
                <div className="md:col-span-2">
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-2 block tracking-widest">
                    Membre
                  </label>
                  <Select
                    options={members}
                    styles={customSelectStyles(Boolean(errors.memberId))}
                    isClearable
                    placeholder="Rechercher un membre..."
                    value={
                      members.find((m) => m.value === contribution.memberId) ||
                      null
                    }
                    onChange={(selected) => {
                      setContribution({
                        ...contribution,
                        memberId: selected ? selected.value : undefined,
                      });
                      if (errors.memberId)
                        setErrors((prev) => ({ ...prev, memberId: undefined }));
                    }}
                    noOptionsMessage={() => "Aucun membre trouvé"}
                    menuPortalTarget={document.body}
                  />
                  {errors.memberId && (
                    <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                      {errors.memberId}
                    </span>
                  )}
                </div>

                {/* Type de don (Select Intelligent avec Portal) */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-2 block tracking-widest">
                    Type de don
                  </label>
                  <Select
                    options={types}
                    styles={customSelectStyles(Boolean(errors.typeId))}
                    placeholder="Type..."
                    value={
                      types.find((t) => t.value === contribution.typeId) || null
                    }
                    onChange={(selected) => {
                      setContribution({
                        ...contribution,
                        typeId: selected ? selected.value : undefined,
                      });
                      if (errors.typeId)
                        setErrors((prev) => ({ ...prev, typeId: undefined }));
                    }}
                    noOptionsMessage={() => "Aucun type trouvé"}
                    menuPortalTarget={document.body}
                  />
                  {errors.typeId && (
                    <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                      {errors.typeId}
                    </span>
                  )}
                </div>

                {/* Montant (FCFA) */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-2 block tracking-widest">
                    Montant (FCFA)
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={contribution.amount === 0 ? "" : contribution.amount}
                    onChange={(e) => {
                      handleChange(e);
                      if (errors.amount)
                        setErrors((prev) => ({ ...prev, amount: undefined }));
                    }}
                    placeholder="0"
                    className={`w-full bg-[#0F172A] border ${errors.amount ? "border-red-500" : "border-slate-700"} rounded-lg px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all`}
                  />
                  {errors.amount && (
                    <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                      {errors.amount}
                    </span>
                  )}
                </div>

                {/* Date */}
                <div className="md:col-span-2">
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-2 block tracking-widest">
                    Date de paiement
                  </label>
                  <input
                    type="date"
                    name="paymentDate"
                    value={contribution.paymentDate}
                    onChange={handleChange}
                    className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all [color-scheme:dark]"
                  />
                </div>

                {/* Observation */}
                <div className="md:col-span-2">
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-2 block tracking-widest">
                    Observation
                  </label>
                  <textarea
                    name="observation"
                    value={contribution.observation}
                    onChange={handleChange}
                    className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all h-20 resize-none"
                  />
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-4 border-t border-slate-700/50 pt-6">
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  className="text-slate-400 font-bold text-xs uppercase px-4 py-2 hover:text-white transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
